import { CompactEncrypt, importSPKI } from 'jose';

// How long a browser-encrypted secure input stays decryptable; the MCP server
// rejects expired ciphertext.
const SECURE_INPUT_TTL_SECONDS = 300;

const JWE_ALGORITHM = 'RSA-OAEP-256';
const JWE_ENCRYPTION = 'A256GCM';

export interface SecureInputKey {
  kid: string;
  publicKeyPem: string;
}

export interface SecureInputContext {
  tool: string;
  conversationId: string;
  // Target ids (e.g. audience_id) the ciphertext is bound to; the MCP server
  // rejects the blob when they don't match the actual tool call arguments.
  bindings: Record<string, string>;
}

// Encrypts one sensitive field's value as a JWE compact serialization bound to its
// conversation/tool/target context. The plaintext value never leaves the browser
// in any other form: model context, tool arguments, and telemetry only ever see
// this ciphertext.
export const encryptSecureInput = async (
  key: SecureInputKey,
  value: string,
  context: SecureInputContext,
): Promise<string> => {
  const publicKey = await importSPKI(key.publicKeyPem, JWE_ALGORITHM);
  const payload = {
    value,
    tool: context.tool,
    conversationId: context.conversationId,
    bindings: context.bindings,
    expiresAt: Math.floor(Date.now() / 1000) + SECURE_INPUT_TTL_SECONDS,
  };

  return new CompactEncrypt(new TextEncoder().encode(JSON.stringify(payload)))
    .setProtectedHeader({ alg: JWE_ALGORITHM, enc: JWE_ENCRYPTION, kid: key.kid })
    .encrypt(publicKey);
};
